import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { EALogo } from "@/components/brand/EALogo";

const links = [
  { label: "Thesis", href: "#manifesto" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Prospectus", href: "#prospectus" },
  { label: "Bond", href: "#bond" },
  { label: "Careers", href: "#careers" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink-950/70 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <span className="text-spectrum-green">
            <EALogo size={28} strokeWidth={7} />
          </span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/60 group-hover:text-white transition-colors">
            Svenska Invest / EA
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white link-underline pb-0.5"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="#bond"
            className="group relative inline-flex items-center gap-2 border border-spectrum-green/60 bg-spectrum-green/10 px-4 py-2 font-mono text-[11px] tracking-[0.25em] uppercase text-spectrum-green hover:bg-spectrum-green hover:text-ink-950 transition-colors"
            data-testid="nav-cta-invest"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-spectrum-green animate-pulseGlow" />
            Invest
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-white p-2"
          aria-label="menu"
          data-testid="nav-mobile-toggle"
        >
          <div className={`w-6 h-px bg-white transition-all ${open ? "rotate-45 translate-y-[2px]" : ""}`} />
          <div className={`w-6 h-px bg-white mt-1.5 transition-all ${open ? "-rotate-45 -translate-y-[2px]" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-ink-950/95 border-t border-white/5 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-mono text-sm uppercase tracking-[0.25em] text-white/70"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
